import { useNavigate } from 'react-router-dom'
import { X, Loader2, Image, Package } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { useTaskManager, type TaskState } from '@/contexts/TaskManagerContext'
import { cn } from '@/lib/utils'
import { getTaskLabel } from '@/lib/task-labels'

interface TaskProgressCardProps {
  task: TaskState
  /** Compact mode for dropdown panel */
  compact?: boolean
}

export function TaskProgressCard({ task, compact = false }: TaskProgressCardProps) {
  const { cancelTask } = useTaskManager()
  const navigate = useNavigate()

  const canCancel = task.status === 'pending'
  const isProcessing = task.status === 'processing'

  const handleNavigate = () => {
    if (task.entityType === 'design') {
      navigate(`/designs/${task.entityId}`)
    } else if (task.entityType === 'batch') {
      navigate(`/batches/${task.entityId}`)
    }
  }

  const handleCancel = async (e: React.MouseEvent) => {
    e.stopPropagation()
    await cancelTask(task.taskId)
  }

  const Icon = task.entityType === 'batch' ? Package : Image
  const taskTypeLabel = getTaskLabel(task.taskType, 'progress')

  return (
    <div
      className={cn(
        'rounded-lg border bg-card transition-colors hover:bg-muted/50',
        compact ? 'p-2' : 'p-3'
      )}
    >
      <div className="flex items-start gap-3">
        {/* Icon with spinning loader overlay when processing */}
        <div className="relative flex-shrink-0">
          <div
            className={cn(
              'flex items-center justify-center rounded-md bg-muted',
              compact ? 'h-8 w-8' : 'h-10 w-10'
            )}
          >
            <Icon className={cn('text-muted-foreground', compact ? 'h-4 w-4' : 'h-5 w-5')} />
          </div>
          {isProcessing && (
            <div className="absolute -bottom-1 -right-1 rounded-full bg-card p-0.5">
              <Loader2 className="h-3 w-3 animate-spin text-primary" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          {/* Task type label and name */}
          <div className="flex items-center gap-1.5">
            <Badge variant="default" className={cn('whitespace-nowrap', compact ? 'text-[10px]' : 'text-xs')}>
              {taskTypeLabel}
            </Badge>
            <button
              onClick={handleNavigate}
              className={cn(
                'truncate font-medium hover:underline',
                compact ? 'text-sm' : 'text-sm'
              )}
            >
              {task.displayName ?? task.entityId.slice(0, 8)}
            </button>
          </div>

          {/* Progress message */}
          <p className={cn('truncate text-muted-foreground', compact ? 'text-xs' : 'text-xs')}>
            {task.progressMessage}
          </p>

          {/* Progress bar */}
          <div className="mt-2 flex items-center gap-2">
            <Progress value={task.progress} className={cn(compact ? 'h-1' : 'h-1.5')} />
            <span className="flex-shrink-0 text-xs text-muted-foreground">{task.progress}%</span>
          </div>

          {/* Batch progress details */}
          {task.batchProgress && (
            <p className="mt-1 text-xs text-muted-foreground">
              {task.batchProgress.completed} of {task.batchProgress.total} designs
              {task.batchProgress.failed > 0 && (
                <span className="text-destructive"> ({task.batchProgress.failed} failed)</span>
              )}
            </p>
          )}
        </div>

        {/* Cancel button (only for pending tasks) */}
        {canCancel && (
          <Button
            variant="ghost"
            size="icon"
            className={cn('flex-shrink-0', compact ? 'h-6 w-6' : 'h-7 w-7')}
            onClick={(e) => {
              void handleCancel(e)
            }}
            title="Cancel task"
          >
            <X className={cn(compact ? 'h-3 w-3' : 'h-4 w-4')} />
          </Button>
        )}
      </div>
    </div>
  )
}
